import { forwardRef, useState, useEffect } from 'react';
import { Input, type InputProps } from '../../atoms/Input';
import './SearchBar.css';

export interface SearchBarProps extends Omit<InputProps, 'type' | 'iconLeft' | 'clearable'> {
  /**
   * Debounce delay in milliseconds
   * @default 300
   */
  debounce?: number;

  /**
   * Callback when search value changes (after debounce)
   */
  onSearch?: (value: string) => void;

  /**
   * Show loading indicator
   * @default false
   */
  loading?: boolean;
}

/**
 * SearchBar component with built-in search icon and debounced search
 *
 * @example
 * ```tsx
 * <SearchBar
 *   placeholder="Search users..."
 *   onSearch={(value) => console.log('Searching:', value)}
 *   debounce={500}
 * />
 * ```
 */
export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(({
  debounce = 300,
  onSearch,
  loading = false,
  value: controlledValue,
  onChange,
  onClear,
  ...props
}, ref) => {
  const [internalValue, setInternalValue] = useState(controlledValue || '');

  // Sync with controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  // Debounced search
  useEffect(() => {
    if (!onSearch) return;

    const timer = setTimeout(() => {
      onSearch(String(internalValue));
    }, debounce);

    return () => clearTimeout(timer);
  }, [internalValue, debounce, onSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);

    if (onChange) {
      onChange(e);
    }
  };

  const handleClear = () => {
    setInternalValue('');

    if (onClear) {
      onClear();
    }

    if (onSearch) {
      onSearch('');
    }
  };

  const searchIcon = loading ? (
    <span className="search-spinner" />
  ) : (
    <span>🔍</span>
  );

  return (
    <Input
      ref={ref}
      type="search"
      value={controlledValue !== undefined ? controlledValue : internalValue}
      onChange={handleChange}
      onClear={handleClear}
      iconLeft={searchIcon}
      clearable={Boolean(internalValue)}
      {...props}
    />
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
